import dayjs from "dayjs";

import { dateRegex } from "../../../../../../utils/constants/dateRegex";
import { getTSeparatedDate } from "../../../../../../utils/methods/dateAndTimeMethods";
import { isTimeTBD } from "../../../../../../appCalcutions";
import {
  datesForDropDown,
  overnightTimeOut,
  timeInAndTimeOutFromStartDate,
} from "../../sessionHandlers";
import type {
  PositionFormValues,
  SessionDates,
  ShiftTimeInfo,
} from "./positionForm.types";

/**
 * Pure helpers shared by every part of the Add/Edit Position form.
 * Nothing in here touches React or Formik: each helper takes plain values and
 * returns plain values, so the same rule is applied the same way everywhere.
 */

export const TBD = "TBD";
export const DATE_KEY_FORMAT = "YYYY-MM-DD";

export const ATTIRE_OPTIONS = ["Blacks (Shorts OK)", "Tech Blacks", "Show Blacks", "Dress Blacks"];
export const COMPLEXITY_OPTIONS = ["none", "low", "medium", "high"];

/**
 * Shift fields shared by a `positionTimeInfo` row and by the edit form, where
 * the single shift lives on the root of the values.
 */
type TimeFields = Partial<
  Pick<ShiftTimeInfo, "dateStart" | "timeIn" | "timeOut" | "tbd" | "numberOfHours" | "overNightShift">
>;

export const createEmptyShiftRow = (overrides: Partial<ShiftTimeInfo> = {}): ShiftTimeInfo => ({
  isSelectedFromCalendar: false,
  dateStart: "",
  timeIn: "",
  timeOut: "",
  tbd: false,
  numberOfHours: "",
  quantity: "",
  overNightShift: false,
  option: "",
  ...overrides,
});

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/**
 * A shift date is a calendar day, not an instant. The edit flow receives it as
 * UTC midnight ("2026-10-30T00:00:00.000Z"), which local time (Brazil, US)
 * reads as the day before. Strings are therefore read by their date part.
 */
export const toDay = (value: dayjs.ConfigType): dayjs.Dayjs =>
  typeof value === "string" && DATE_PREFIX.test(value) ? dayjs(value.slice(0, 10)) : dayjs(value);

export const toDateKey = (value: dayjs.ConfigType): string => toDay(value).format(DATE_KEY_FORMAT);

/** Every calendar day of the session, as dayjs objects (start and end included). */
export const getSessionDates = (session?: SessionDates): dayjs.Dayjs[] =>
  datesForDropDown(session?.sessionStart, session?.sessionEnd) ?? [];

/** Every calendar day of the session, as `YYYY-MM-DD` keys. */
export const getSessionDateKeys = (session?: SessionDates): string[] =>
  getSessionDates(session).map((day) => day.format(DATE_KEY_FORMAT));

export const isSameDay = (a: dayjs.ConfigType, b: dayjs.ConfigType): boolean => {
  if (!a || !b) return false;
  const first = toDay(a);
  const second = toDay(b);
  return first.isValid() && second.isValid() && first.isSame(second, "day");
};

// ---------------------------------------------------------------------------
// Times (stored as local `YYYY-MM-DDTHH:mm:ss` strings, or "TBD")
// ---------------------------------------------------------------------------

const getTimeParts = (dateTime?: string | null) => {
  const [hour, minute] = dateTime?.split?.("T")?.[1]?.split?.(":") ?? [];
  return { hour, minute };
};

/**
 * Builds the stored date-time for `day` at `hour:minute`, moved to the next day
 * for overnight end times. Returns "" when the result is not a valid date.
 */
export const buildDateTime = (
  day: dayjs.ConfigType,
  hour: string | number | undefined,
  minute: string | number | undefined,
  { nextDay = false }: { nextDay?: boolean } = {},
): string => {
  const date = toDay(day).toDate();
  if (nextDay) date.setDate(date.getDate() + 1);
  date.setHours(Number(hour));
  date.setMinutes(Number(minute));
  const formatted = getTSeparatedDate(date);
  return dateRegex.test(formatted.split("T")[0]) ? formatted : "";
};

/** Picker value for a stored time, shown on the shift's own day. */
export const toPickerValue = (day?: string, dateTime?: string | null): dayjs.Dayjs | null => {
  if (!day || !dateTime) return null;
  const { hour, minute } = getTimeParts(dateTime);
  const date = toDay(day).toDate();
  date.setHours(Number(hour));
  date.setMinutes(Number(minute));
  return dayjs(date);
};

/** Returns the shift with its start time set from a picker value. */
export const withTimeIn = <T extends TimeFields>(
  row: T,
  picked: dayjs.Dayjs | null,
  fallbackDay?: string,
): T => {
  const timeIn = buildDateTime(row.dateStart || fallbackDay, picked?.hour(), picked?.minute());
  return { ...row, timeIn, dateStart: row.dateStart || timeIn.split("T")[0] };
};

/**
 * Returns the shift with its end time set. Times are typed in 24h, so the
 * hour is stored exactly as picked.
 */
export const withTimeOut = <T extends TimeFields>(
  row: T,
  endHour: number | undefined,
  minute: number | undefined,
  fallbackDay?: string,
): T => {
  const timeOut = buildDateTime(row.dateStart || fallbackDay, endHour, minute, {
    nextDay: !!row.overNightShift,
  });
  return { ...row, timeOut, dateStart: row.dateStart || timeOut.split("T")[0] };
};

export const withTbd = <T extends TimeFields>(row: T, tbd: boolean): T => ({
  ...row,
  tbd,
  numberOfHours: "",
  timeIn: tbd ? TBD : "",
  timeOut: tbd ? TBD : "",
});

/** Moves the shift to another day, keeping its start and end times. */
export const withDateStart = <T extends TimeFields>(row: T, dateStart: string): T => {
  const moved = row.timeIn && row.timeOut ? timeInAndTimeOutFromStartDate(row, dateStart) : null;
  return { ...row, dateStart, ...(moved || {}) };
};

/** Turns overnight on/off and moves an existing end time to the right day. */
export const withOvernight = <T extends TimeFields>(row: T, overnight: boolean): T => ({
  ...row,
  overNightShift: overnight,
  timeOut: isTimeTBD(row.timeOut)
    ? row.timeOut
    : overnightTimeOut({ ...row, dateStart: row.dateStart && toDateKey(row.dateStart) }, overnight) ||
      row.timeOut,
});

// ---------------------------------------------------------------------------
// Calendar view (bulk inputs applied to every date picked on the calendar)
// ---------------------------------------------------------------------------

/** Time fields a calendar-selected date inherits from the bulk inputs. */
const calendarTimesFor = (
  day: string,
  overnight: boolean,
  values: PositionFormValues,
): Partial<ShiftTimeInfo> => {
  if (values?.calendarTBD) {
    return {
      tbd: true,
      timeIn: TBD,
      timeOut: TBD,
      numberOfHours: values?.calendarNumberOfHours ?? "",
    };
  }
  const start = getTimeParts(values?.calendarTimeIn);
  const end = getTimeParts(values?.calendarTimeOut);
  return {
    timeIn: buildDateTime(day, start.hour, start.minute),
    timeOut: buildDateTime(day, end.hour, end.minute, { nextDay: overnight }),
  };
};

const byDate = (a: ShiftTimeInfo, b: ShiftTimeInfo) =>
  new Date(a?.dateStart).getTime() - new Date(b?.dateStart).getTime();

const calendarRowsFirst = (a: ShiftTimeInfo, b: ShiftTimeInfo) =>
  Number(!!b?.isSelectedFromCalendar) - Number(!!a?.isSelectedFromCalendar);

/**
 * Applies a new calendar selection. Removed dates drop their rows; added dates
 * get a row pre-filled from the bulk inputs (times, TBD, quantity, overnight).
 */
export const applyCalendarSelection = (
  values: PositionFormValues,
  pickedDates: dayjs.ConfigType[] = [],
): PositionFormValues => {
  const nextDates = [...new Set((pickedDates ?? []).map(toDateKey))].sort();
  const previousDates = values?.calendarViewDates ?? [];
  const rows = values?.positionTimeInfo ?? [];

  if (nextDates.length <= previousDates.length) {
    return {
      ...values,
      calendarViewDates: nextDates,
      positionTimeInfo: rows.filter(
        (row) => !row?.isSelectedFromCalendar || nextDates.includes(row?.dateStart),
      ),
    };
  }

  const keepsIndividualTimes = (row: ShiftTimeInfo) =>
    !values?.eachDateOfSession || !row?.isSelectedFromCalendar || row?.tbd || values?.calendarTBD;

  const existingRows = rows.map((row) =>
    keepsIndividualTimes(row)
      ? row
      : { ...row, ...calendarTimesFor(row.dateStart, !!row.overNightShift, values) },
  );

  const addedRows = nextDates
    .filter((day) => !previousDates.includes(day))
    .map((day) =>
      createEmptyShiftRow({
        isSelectedFromCalendar: true,
        dateStart: day,
        quantity: values?.calendarQuantity || "",
        overNightShift: !!values?.calendarOvernight,
        ...calendarTimesFor(day, !!values?.calendarOvernight, values),
      }),
    );

  return {
    ...values,
    calendarViewDates: nextDates,
    positionTimeInfo: [...existingRows, ...addedRows].sort(byDate).sort(calendarRowsFirst),
  };
};

/** Removes one row and, for a calendar row, un-selects its date as well. */
export const removeShiftRow = (values: PositionFormValues, rowIndex: number): PositionFormValues => {
  const row = values?.positionTimeInfo?.[rowIndex];
  return {
    ...values,
    positionTimeInfo: (values?.positionTimeInfo ?? []).filter((_, idx) => idx !== rowIndex),
    calendarViewDates: row?.isSelectedFromCalendar
      ? (values?.calendarViewDates ?? []).filter((day) => day !== row?.dateStart)
      : values?.calendarViewDates,
  };
};

// ---------------------------------------------------------------------------
// "Manage positions in depth" sheet
// ---------------------------------------------------------------------------

const toSheetTime = (value?: string) => {
  if (!value) return null;
  return dayjs(value).isValid() && !isTimeTBD(value) ? value : TBD;
};

/** One sheet row per position x date x unit of quantity. */
export const buildManageSheetRows = (values: PositionFormValues, session: SessionDates) => {
  if (!values?.positions || !values?.positionTimeInfo) return [];

  let key = 0;
  return values.positions.flatMap((position) =>
    values.positionTimeInfo.flatMap((timeInfo) =>
      Array.from({ length: Number(timeInfo?.quantity || 1) }, () => {
        key += 1;
        return {
          _id: key,
          positionId: position?._id,
          positionName: position?.positionName,
          session,
          startDate: timeInfo?.dateStart,
          timeIn: toSheetTime(timeInfo?.timeIn),
          timeOut: toSheetTime(timeInfo?.timeOut),
          tbd: timeInfo?.tbd,
          numberOfHours: timeInfo?.numberOfHours,
          quantity: 1,
          overNightShift: timeInfo?.overNightShift,
          selectOption: values?.option,
          additionalComments: values?.comments,
          complexity: values?.complexity,
          attire: values?.attire,
        };
      }),
    ),
  );
};
